/**
 * OTL pre-order — CART. Group pre-order lines by style+colour, block checkout
 * below the 100pc minimum, and tell the customer exactly what to do about it.
 *
 * Depends on otl-preorder-core.js (window.OTLPreorderCore) for all maths.
 *
 * ── WHY IT'S BUILT THIS WAY (Retina 6.3.0 specifics, verified by reading the bundle)
 *
 * 1. There are TWO checkout buttons and they behave DIFFERENTLY:
 *      - Cart page  (sections/cart-template.liquid):  Retina's `Cart` class binds
 *        a bubble-phase click on [data-cart-submit] and calls form.submit().
 *        → beat it with a CAPTURE listener + stopImmediatePropagation().
 *      - Cart drawer (sections/cart-drawer-template.liquid): NOTHING binds it —
 *        it's a native submit inside <form action="/cart" method="post">.
 *        → a capture click listener alone is not enough; also cancel the form's
 *          `submit` event, or an Enter keypress walks straight past us.
 *    Both surfaces reuse id="checkout" and id="cart_form", so the DOM contains
 *    DUPLICATE IDs — never select by #id here; scope by class/attribute.
 *
 * 2. The drawer's markup is REPLACED wholesale on every add (both Retina's
 *    loadDrawer and theme.liquid's OTL auto-open patch call innerHTML = ...).
 *    So every listener is delegated from `document`, never bound to drawer nodes.
 *
 * 3. FAIL OPEN. If anything in here throws, checkout must still work — the Phase 5
 *    checkout validation function is the hard, server-side backstop. This script
 *    is UX, not enforcement. Every entry point is wrapped accordingly.
 */
(function () {
  'use strict';

  var core = window.OTLPreorderCore;
  if (!core) return;

  var cfg = window.OTLPreorderConfig || {};
  var MINIMUM = cfg.minimum || core.DEFAULT_MINIMUM;

  function log() {
    if (!cfg.debug) return;
    try { console.log.apply(console, ['[otl-preorder-cart]'].concat([].slice.call(arguments))); } catch (e) {}
  }

  /** Are we on the cart PAGE? Ask the URL — the DOM lies (duplicate #cart_form). */
  function onCartPage() {
    return /\/cart\/?$/.test(location.pathname);
  }

  /**
   * ⚠️ EVERY CART WRITE IS BOUNDED — for the same reason getCart() is.
   *
   * The reads got a timeout because "a hung fetch used to wedge the whole gate".
   * The WRITES never did, and they wedge more: they are serialised onto one chain,
   * so a single stalled /cart/add on a dropped mobile connection meant the chain
   * never drained — every later stepper write queued behind it forever, the pending
   * counter never released, closeModal's flush never resolved (so nothing ever
   * chased the drawer), and "Continue to checkout" stayed disabled for the life of
   * the page with no way back but a reload.
   *
   * Generous (12s) so it only ever fires on a genuinely dead connection. An aborted
   * write that did commit server-side is recoverable — the failure path re-reads the
   * cart and adopts the true line keys — whereas the wedge is not recoverable at all.
   */
  var CART_WRITE_TIMEOUT_MS = 12000;
  var PRODUCT_READ_TIMEOUT_MS = 8000;
  var DRAWER_READ_TIMEOUT_MS = 8000;

  /**
   * ⚠️ EVERY fetch in this file goes through here. There are no exceptions left,
   * and there must not be new ones.
   *
   * Four separate wedges were found in four rounds, all the same shape: an
   * unbounded request (or an unbounded BODY read behind a bounded request) that
   * never settles, latching a `checking` / `drawerSyncing` / `productInflight`
   * flag on forever and taking a whole feature down for the life of the page. The
   * fix kept being applied to one call site at a time while its siblings kept the
   * bug — so the helper is now the only way to make a request.
   *
   * It resolves to { ok, status, statusText, text } and NEVER to a Response,
   * because handing back a Response is what let the body be read outside the
   * timeout's reach.
   */
  function fetchBounded(url, opts, timeoutMs) {
    var ctrl = typeof AbortController !== 'undefined' ? new AbortController() : null;
    var timer = ctrl ? setTimeout(function () { ctrl.abort(); }, timeoutMs) : null;
    var o = {};
    for (var k in opts) { if (Object.prototype.hasOwnProperty.call(opts, k)) o[k] = opts[k]; }
    if (ctrl) o.signal = ctrl.signal;
    /**
     * ⚠️ THE BODY IS READ INSIDE THE TIMEOUT, and the timer cleared only after.
     *
     * fetch() resolves when the response HEADERS arrive; the body arrives later.
     * Clearing the abort timer in that first handler throws away the only thing
     * that could kill a stalled body stream — ctrl.abort() does abort an
     * in-progress read, but by then it can never fire. Proven, not reasoned: a
     * server that sends 200 headers and never finishes the body leaves the
     * clear-on-headers shape pending forever, and this shape rejecting on time.
     */
    var done = fetch(url, o).then(function (r) {
      return r.text().then(function (text) {
        if (timer) clearTimeout(timer);
        return { ok: r.ok, status: r.status, statusText: r.statusText, text: text };
      }, function (err) {
        if (timer) clearTimeout(timer);
        throw err;
      });
    }, function (err) {
      if (timer) clearTimeout(timer);
      throw err;
    });
    /**
     * ⚠️ THE RACE RUNS UNCONDITIONALLY — not only when AbortController is absent.
     *
     * This helper used to gate it on `if (!ctrl)`, while its two siblings
     * (otl-preorder.js, theme.liquid) run it always, citing Safari 11 and Edge 16/17:
     * they HAVE the AbortController constructor but IGNORE `signal` on fetch, so
     * abort() is a no-op and the deadline silently does not exist. Both helpers were
     * lifted and run side by side against a stalled body on a browser matching that
     * premise: the sibling rejected on time, this one was still pending at 5s. The
     * two files cannot both be right, and this is the file where an unsettled promise
     * latches `checking` / `drawerSyncing` / `productInflight` and leaves "Continue to
     * checkout" disabled for the life of the page.
     *
     * The race timer is cleared on both settle paths, so an on-time response costs
     * nothing.
     */
    var raceTimer = null;
    return Promise.race([
      done,
      new Promise(function (_, reject) {
        raceTimer = setTimeout(function () {
          reject(new Error('request timed out: ' + url));
        }, timeoutMs + 500);
      }),
    ]).then(function (v) { if (raceTimer) clearTimeout(raceTimer); return v; },
            function (e) { if (raceTimer) clearTimeout(raceTimer); throw e; });
  }

  /**
   * ⚠️ "Could not read it" must never look like "there was nothing there".
   *
   * A login-gated storefront answers a lapsed session or a bot-check with a 200
   * carrying HTML. Swallowing that into `null` made an unreadable body
   * indistinguishable from an empty one — and on the add path that meant reporting
   * SUCCESS WITH NO LINE KEY, which sends the next edit down the add path where
   * Shopify SUMS. So parseability is reported separately and callers must check it.
   */
  function parseJsonBody(text) {
    if (!text) return { parsed: false, value: null };
    try { return { parsed: true, value: JSON.parse(text) }; } catch (e) {
      return { parsed: false, value: null };
    }
  }

  function postCart(url, body) {
    var opts = {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify(body),
    };
    // NB: still goes through window.fetch, so theme.liquid's /cart/add patch — the
    // thing that re-opens the drawer — sees it exactly as before.
    return fetchBounded(url, opts, CART_WRITE_TIMEOUT_MS).then(function (res) {
      var b = parseJsonBody(res.text);
      return {
        ok: res.ok, status: res.status, statusText: res.statusText,
        body: b.value, parsed: b.parsed,
      };
    });
  }

  /** Set one line's quantity by its stable key. Never by line index. */
  function setLineQuantity(key, quantity) {
    return postCart('/cart/change.js', {
      id: key,
      quantity: Math.max(0, parseInt(quantity, 10) || 0),
    }).then(function (res) {
      /**
       * A stale line key 404s, and without this check that resolved as success —
       * so a size's ships-now half could silently not be written while the
       * made-to-order half was, and the dead key stayed on the row so every later
       * edit of that size no-oped too. Surface it; the caller heals by dropping the
       * key and re-adding.
       */
      if (!res.ok) {
        var err = new Error('cart/change ' + res.status);
        err.otlStatus = res.status;
        throw err;
      }
      /**
       * ⚠️ A 200 we cannot read is a FAILURE, not an empty success. Returning it as
       * success let put() run `if (quantity <= 0) removeAttribute(keyAttr)` and drop
       * the key of a line still sitting in the cart, because the change never
       * happened — so stepping that size back up added on top of it and Shopify
       * summed the two. Reject instead; flushRow's catch re-reads the cart and
       * repairs the keys from it.
       */
      if (!res.parsed) throw new Error('cart/change: unreadable body');
      return res.body;
    });
  }

  /**
   * A hung read used to wedge the whole gate: `checking` stayed true forever, so
   * every later refresh was queued and never ran — and because express fails closed,
   * a wedge ALSO stranded the cart page with no express for its whole life.
   *
   * Bounded through fetchBounded, and the body checked three ways below: `ok`,
   * parseable, and actually carrying `items`. A 4xx/5xx HTML error page satisfies
   * none of them and is rejected outright rather than half-parsed into something
   * that looks like a cart.
   */
  var CART_READ_TIMEOUT_MS = 8000;

  function getCart() {
    var opts = { credentials: 'same-origin', headers: { Accept: 'application/json' } };
    return fetchBounded('/cart.js', opts, CART_READ_TIMEOUT_MS).then(function (res) {
      if (!res.ok) throw new Error('cart read HTTP ' + res.status);
      var b = parseJsonBody(res.text);
      /**
       * Checked outright rather than hoped for: a 4xx/5xx HTML page used to be left
       * to `r.json()`, which either threw or — worse — yielded something without
       * `.items` that the rest of the file would treat as a cart. `items` on an
       * empty cart is `[]`, which is truthy, so this does not reject a legitimately
       * empty cart.
       */
      if (!b.parsed) throw new Error('cart read: unreadable body');
      if (!b.value || !b.value.items) throw new Error('cart read: no items in body');
      return b.value;
    });
  }

  // ── modal ─────────────────────────────────────────────────────────────────
  /**
   * Groups the modal was opened with. Declared up here because closeModal() clears
   * it and runs before the run-builder block below. (`var` hoists, but reading a
   * name defined 400 lines later is exactly how the last dead-code bug hid.)
   */
  var openedGroups = [];

  /**
   * Set by init() to the builder's live recount. Module-level because
   * upgradeSections has to re-run it after restoring a carried-over value, and
   * liveRecount itself is scoped to init(). A no-op until wiring completes, so an
   * early call can never throw.
   */
  var liveRecountSafe = function () {};

  /**
   * Set by init() to the builder's pending-write flush. Closing the panel is not a
   * cancel gesture — see closeModal.
   */
  var flushPendingSafe = function () { return Promise.resolve(); };

  /**
   * True when the customer is owed a cart write we have not finished — a debounce
   * still counting down, a write queued or running, or an /cart/add that landed
   * recently enough that theme.liquid may still be about to re-open the drawer.
   *
   * ⚠️ NOT "waiting on its debounce". That is what this comment used to say, and it
   * is precisely the misconception that produced two rounds of bugs: the timer is
   * deleted the instant it fires, long before the write goes out. See the note over
   * `outstandingWrites` in the run builder.
   */
  var pendingWorkExists = function () { return false; };

  function closeModal() {
    /**
     * ⚠️ FLUSH FIRST. Closing the panel is not a cancel gesture.
     *
     * flushRow resolves its row with liveInputFor(), which finds nothing once the
     * modal has left the DOM — so a write still inside the 450ms debounce was
     * silently dropped. Tap "+" until the bar reads 100/100 (liveRecount shows it
     * instantly) and press Escape a moment later, and the cart stayed 10 short
     * while the screen had just said you were done.
     */
    /**
     * ...and the flush's own write RE-OPENS the drawer. theme.liquid answers a
     * successful /cart/add with `setTimeout(refreshAndOpenDrawer, 250)`, and that
     * function fetches twice more before it opens — so closing the drawer below
     * (which happens immediately) is far too early: the drawer appears afterwards,
     * over the page the customer just asked to return to.
     *
     * Two fixed shots used to sit here and were deleted in round 6; this comment
     * went on describing them for two more rounds. There is ONE deferred action now:
     * WATCH for the drawer, bounded and close-once, when we are the reason it is
     * coming.
     *
     * ⚠️ And NOTHING deferred when we are not. An `else closeCartDrawer()` used to
     * sit here and was worse than useless: the synchronous closeCartDrawer() below
     * has already shut anything open, and closeCartDrawer early-returns when the
     * drawer is closed — so it was a no-op on nearly every path. On the paths where
     * it was not, it was a delayed slam: releaseWrite fires when the write settles,
     * before the refreshes, so `hadPendingWrite` can read false while the chain
     * stays pending for two cart reads. The customer closes the panel, deliberately
     * opens the drawer seconds later, and this finally resolves and shuts it in
     * their face — exactly the fixed-moment failure closeDrawerIfItReopens exists to
     * avoid. A false `hadPendingWrite` means no timers, no outstanding writes and no
     * recent add, so nothing of ours can re-open the drawer: there is nothing to do.
     */
    var hadPendingWrite = pendingWorkExists();
    flushPendingSafe().then(function () {
      // Only chase the drawer when OUR write is what re-opens it.
      if (hadPendingWrite) closeDrawerIfItReopens();
    }, function () {});

    var m = document.querySelector('[data-otl-preorder-modal]');
    if (m && m.parentNode) m.parentNode.removeChild(m);

    /**
     * The class was HIDING a drawer, not closing one: theme.liquid re-opens it on
     * a timer after every /cart/add, and each stepper write issues up to two. Drop
     * the class alone and a fully-open drawer — overlay and scroll lock included —
     * is revealed over the page the customer expected to come back to.
     */
    document.body.classList.remove('otl-preorder-modal-open');
    closeCartDrawer();

    // Forget what we opened with, or the next refresh would rebuild a panel the
    // customer has just dismissed.
    openedGroups = [];
  }

  /**
   * ── THE RUN BUILDER ───────────────────────────────────────────────────────
   *
   * Every size in the colour on one screen, with a progress bar over them, instead
   * of a stepper for the one size already in the cart.
   *
   * WHY (measured, not assumed): across 2000 real orders and 4618 style+colour
   * groups, 79 groups reached 100+ units and **78 of them spanned more than one
   * size**. Exactly one order in two thousand wanted 100+ of a single size. A modal
   * that edits one size at a time is the wrong shape for how these customers buy.
   *
   * The size list comes from /products/<handle>.js, NOT from the cart — the whole
   * point is to offer sizes that are not in the cart yet. That fetch is async, so
   * the panel paints immediately from cart data and upgrades in place when the
   * product lands. A customer must never wait on a network round-trip to see why
   * their checkout was stopped.
   */
  var productCache = {};
  var productInflight = {};

  function loadProductForGroup(g) {
    if (!g || !g.handle) return Promise.resolve(null);
    var h = g.handle;
    if (productCache[h]) return Promise.resolve(productCache[h]);
    if (productInflight[h]) return productInflight[h];
    /**
     * ⚠️ BOUNDED, because a stall here does not degrade the builder — it BRICKS it.
     *
     * This was the one request in the file with no timeout of any kind. On a stall
     * neither handler ran, so `productInflight[h]` was never deleted and every later
     * call returned the same pending promise. The panel falls back to `degraded`
     * rows, which render the input `readonly` and both steppers `disabled` — correct
     * when stock is unknown, but now PERMANENT: closing and reopening the builder,
     * or opening it from another surface, hit the same wedged entry. The customer
     * sees a panel they cannot edit, cannot reach 100pc through, and cannot check
     * out from, with nothing on screen saying why and no way back but a reload.
     *
     * The delete happens on EVERY outcome, so a retry can actually re-fetch.
     */
    productInflight[h] = fetchBounded(
      '/products/' + encodeURIComponent(h) + '.js',
      { credentials: 'same-origin' },
      PRODUCT_READ_TIMEOUT_MS
    )
      .then(function (res) {
        if (!res.ok) return null;
        return parseJsonBody(res.text).value;
      })
      .then(function (p) {
        if (p) productCache[h] = p;
        delete productInflight[h];
        return p;
      })
      .catch(function (err) {
        delete productInflight[h];
        log('product load failed for ' + h, err);
        return null; // the panel keeps its cart-only rows; it is degraded, not broken
      });
    return productInflight[h];
  }

  /**
   * One stepper row. `key` addresses an existing cart line; a null key means this
   * size is not in the cart yet and the stepper must ADD rather than change.
   *
   * The row carries `data-otl-variant` and `data-otl-color` so the click handler
   * can build an add payload without re-deriving the group it belongs to.
   */
  function sizeRow(row, g) {
    var stock = row.degraded
      ? '<span class="otl-size__stock otl-size__stock--unknown">stock unavailable</span>'
      : (row.available > 0
        ? '<span class="otl-size__stock">' + row.available + ' in stock — those ship now</span>'
        : '<span class="otl-size__stock otl-size__stock--made">made to order</span>');
    // See rowsFor: without stock figures we cannot split, and splitting wrongly
    // orders garments we already own. So the controls are inert until it loads.
    var off = row.degraded ? ' disabled' : '';
    /**
     * The stepper shows the size's TOTAL (ships-now + made), because that is what
     * the customer is choosing. The split happens on write and only the made part
     * counts toward the run — which is why the stock line above says so plainly.
     *
     * Every value the write needs rides on the input: both line keys, the variant,
     * the stock figure, and the group's style/colour. writeQty used to re-derive
     * the stamp by looking the group up in the cart, which returned NOTHING for a
     * run started from the product page — so the first line of a brand-new run was
     * stamped `_style:''` with no `_color` and sat in a different group from every
     * line added after it.
     */
    return '<li class="otl-size" data-otl-size-row>' +
      '<span class="otl-size__name">' + esc(row.size) + '</span>' +
      stock +
      '<span class="otl-preorder-stepper">' +
        '<button type="button" class="otl-preorder-step" data-otl-step="-10"' + off + ' aria-label="ten fewer ' + esc(row.size) + '">−</button>' +
        '<input class="otl-preorder-qty" type="number" min="0" inputmode="numeric" value="' + row.total +
          '"' + (row.degraded ? ' readonly' : '') + ' data-otl-qty' +
          ' data-otl-variant="' + esc(row.variantId) + '"' +
          ' data-otl-avail="' + esc(row.available) + '"' +
          /**
           * What the CART already counts for this size. An untouched row must keep
           * this rather than being re-derived from stock — see liveRecount.
           */
          ' data-otl-pre-units="' + esc(row.preorderUnits) + '"' +
          (row.preorderKey ? ' data-otl-pre-key="' + esc(row.preorderKey) + '"' : '') +
          (row.stockKey ? ' data-otl-stock-key="' + esc(row.stockKey) + '"' : '') +
          ' data-otl-style="' + esc(g.productTitle || '') + '"' +
          ' data-otl-color="' + esc(g.color == null ? '' : g.color) + '"' +
          ' data-otl-group="' + esc(g.key) + '" aria-label="total ' + esc(row.size) + '">' +
        '<button type="button" class="otl-preorder-step" data-otl-step="10"' + off + ' aria-label="ten more ' + esc(row.size) + '">+</button>' +
      '</span>' +
    '</li>';
  }

  /**
   * Rows to show for a group. With product data: every pre-orderable size in the
   * colour. Without it: just the sizes already in the cart, so the panel still
   * works when /products/<handle>.js is unreachable.
   */
  function rowsFor(g, product, items) {
    if (product) return core.runBuilderRows(product, items, g.color);
    return g.lines.map(function (line) {
      return {
        variantId: line.variant_id,
        size: core.sizeOf(line.options_with_values) || line.variant_title || '',
        preorderKey: line.key,
        stockKey: null,
        preorderUnits: line.quantity,
        stockUnits: 0,
        total: line.quantity,
        /**
         * ⚠️ Stock is UNKNOWN here — /products/<handle>.js did not answer — and the
         * previous comment had the safety backwards. `available: 0` does not mean
         * "assume nothing is in stock, safely"; it makes splitRunTotal send the
         * WHOLE total to the made-to-order line. Raise a size from 40 to 100 in a
         * degraded panel with 60 on the shelf and production is asked for 60
         * garments that already exist — the exact rule violation splitRunTotal was
         * written to prevent.
         *
         * So the degraded panel is READ-ONLY. It shows what is in the cart and says
         * why it cannot be edited. Refusing to act on unknown stock is the only
         * honest option; guessing a direction is what caused this.
         */
        available: 0,
        degraded: true,
      };
    });
  }

  function groupSection(g, product, items) {
    var rows = rowsFor(g, product, items);
    return '<li class="otl-preorder-modal__item' + (g.shortfall === 0 ? ' is-ready' : '') +
        '" data-otl-group="' + esc(g.key) + '" data-otl-handle="' + esc(g.handle || '') + '">' +
      core.runProgressHtml(g, MINIMUM) +
      '<ul class="otl-preorder-sizes" data-otl-sizes>' + rows.map(function (r) { return sizeRow(r, g); }).join('') + '</ul>' +
      '<div class="otl-preorder-modal__actions">' +
        '<button type="button" class="otl-preorder-btn otl-preorder-btn--ghost" data-otl-remove-group="' + esc(g.key) + '">Remove this run from cart</button>' +
      '</div>' +
    '</li>';
  }

  /** Modal heading. One run gets named; several get a plural that promises no colour. */
  function modalTitle(groups) {
    if (groups.length === 1) return core.runHeading(groups[0]);
    return 'Finish your made-to-order runs';
  }

  /**
   * @param {Array} groups   groups to show (short ones, plus any that turned green)
   * @param {string} origin  'checkout' (they tried to leave) or 'add' (we caught it early)
   */
  function showModal(groups, origin) {
    closeModal();
    var wrap = document.createElement('div');
    wrap.className = 'otl-preorder-modal';
    wrap.setAttribute('data-otl-preorder-modal', '');
    wrap.setAttribute('data-otl-origin', origin === 'add' ? 'add' : 'checkout');
    wrap.setAttribute('role', 'dialog');
    wrap.setAttribute('aria-modal', 'true');
    wrap.setAttribute('aria-labelledby', 'otl-preorder-modal-title');

    openedGroups = groups.slice();
    var items = (lastCart && lastCart.items) || [];
    wrap.innerHTML =
      '<div class="otl-preorder-modal__backdrop" data-otl-preorder-close></div>' +
      '<div class="otl-preorder-modal__panel">' +
        '<h2 class="otl-preorder-modal__title" id="otl-preorder-modal-title" data-otl-modal-title>' +
          esc(modalTitle(groups)) + '</h2>' +
        '<p class="otl-preorder-modal__lede">Made-to-order pieces need ' + MINIMUM +
          ' per colour. Mix sizes however you like — they all count toward the same ' + MINIMUM + '.</p>' +
        '<ul class="otl-preorder-modal__list" data-otl-group-list>' +
          groups.map(function (g) { return groupSection(g, productCache[g.handle] || null, items); }).join('') +
        '</ul>' +
        '<div class="otl-preorder-modal__footer">' +
          continueButtonHtml(groups) +
          '<button type="button" class="otl-preorder-btn otl-preorder-btn--close" data-otl-preorder-close>' +
            (origin === 'add' ? 'Keep shopping' : 'Back to cart') +
          '</button>' +
        '</div>' +
      '</div>';

    document.body.appendChild(wrap);
    /**
     * A body class as well as closing it: our own /cart/add.js is answered by
     * theme.liquid's patch, which re-opens the drawer on its own timing, so closing
     * it once is a race we lose. The class makes the drawer inert for as long as the
     * builder is up, whatever the theme does underneath.
     */
    document.body.classList.add('otl-preorder-modal-open');
    closeCartDrawer();
    upgradeSections(groups, items);
    var firstQty = wrap.querySelector('[data-otl-qty]');
    if (firstQty) firstQty.focus();
  }

  /**
   * D2 — a way FORWARD. The old modal offered "Keep shopping", "Add a different
   * size" (which navigated away and lost the customer's place) and "Remove from
   * cart". A customer who had just fixed the shortfall had no button that meant
   * "good, take me to checkout" and had to find their way back to the cart.
   *
   * Disabled — not hidden — while anything is short, so the affordance is visible
   * the whole time and the reason sits directly above it in the status line.
   */
  function continueButtonHtml(groups) {
    var short = groups.some(function (g) { return g.shortfall > 0; });
    return '<button type="button" class="otl-preorder-btn otl-preorder-btn--primary" data-otl-continue' +
      (short ? ' disabled aria-disabled="true"' : '') + '>Continue to checkout</button>';
  }

  /**
   * Swap in the full size grid once each group's product data has landed.
   *
   * @param cartItems  The cart the rest of this panel was just drawn from.
   *
   * ⚠️ It must be PASSED IN. This used to reach for the module-level `lastCart`
   * while its only refreshing caller, refreshModal(), had just done its own
   * getCart() and drawn the heading, the Continue button and the group list from
   * THAT read. So one panel was painted from two different snapshots — and
   * `lastCart` is deliberately left stale after a failed read, so the two could be
   * far apart. Two numbers for one cart in one panel is the contradiction this
   * whole phase exists to remove.
   *
   * ⚠️ …which is why there is NO `|| lastCart` fallback below. One briefly sat
   * there and quietly reinstated the defect this contract forbids: it would have
   * painted the size grid from `lastCart` while the rest of the panel came from
   * `cart`. The path that could reach it — a /cart.js body with no `items` — is now
   * rejected by getCart outright, so the fallback would be unreachable as well as
   * wrong; both are reasons not to have one. An absent list means an empty cart
   * here, and says so.
   */
  function upgradeSections(groups, cartItems) {
    groups.forEach(function (g) {
      loadProductForGroup(g).then(function (product) {
        if (!product) return;
        var modal = document.querySelector('[data-otl-preorder-modal]');
        if (!modal) return; // closed while we were fetching
        var section = modal.querySelector('[data-otl-group="' + cssEscape(g.key) + '"]');
        if (!section) return;
        var list = section.querySelector('[data-otl-sizes]');
        if (!list) return;
        /**
         * Never replace the grid out from under someone who is TYPING — but the
         * FIRST upgrade always runs.
         *
         * Caught in the browser: showModal() focuses the placeholder box, this
         * lands a moment later, the focus guard saw that focus and bailed — so the
         * builder never grew past the one size already in the cart. The whole
         * feature was invisible. "A box has focus" and "the customer is editing"
         * are not the same thing, and only the second one is worth protecting.
         */
        var upgraded = section.getAttribute('data-otl-upgraded') === '1';
        if (upgraded && modalInputFocused()) return;

        /**
         * The FIRST upgrade always runs (otherwise the builder never grows past the
         * one size already in the cart) — but it must not eat what the customer has
         * already typed. showModal focuses a box immediately; the product fetch can
         * land 200–500ms later, mid-"120". Carry the value and the caret across.
         */
        /**
         * ⚠️ Carry the VALUE only if the customer has actually edited this box.
         *
         * Carrying it unconditionally re-broke the arithmetic: showModal focuses a
         * box from the degraded first paint, where a size shows only its
         * made-to-order units (3XL = 34). The full grid then renders the correct
         * TOTAL (34 made + 27 in stock = 61) — and the restore immediately wrote 34
         * back over it. liveRecount then subtracted the 27 in stock from an already
         * net figure and the bar read 27 instead of 54. Focus is worth preserving
         * always; a value is worth preserving only when it is theirs.
         */
        var focused = modalInputFocused() ? document.activeElement : null;
        var carry = null;
        if (focused) {
          carry = {
            vid: focused.getAttribute('data-otl-variant'),
            dirty: focused.getAttribute('data-otl-dirty') === '1',
            value: focused.value,
            start: null,
            end: null,
          };
          /**
           * The selection API throws InvalidStateError on input[type="number"] in
           * several engines. Reading it unguarded put that throw on the promise
           * path — swallowed by the upgrade's own catch — and because showModal
           * always focuses a box before the product fetch resolves, EVERY first
           * upgrade would have died on an affected browser: the builder silently
           * stuck at the degraded cart-only rows, on every product. That is the
           * "whole feature was invisible" bug again, by a different route.
           */
          try { carry.start = focused.selectionStart; carry.end = focused.selectionEnd; } catch (e) {}
        }

        var items = cartItems || [];
        list.innerHTML = core.runBuilderRows(product, items, g.color)
          .map(function (r) { return sizeRow(r, g); }).join('');
        section.setAttribute('data-otl-upgraded', '1');

        if (carry && carry.vid) {
          var again = list.querySelector('[data-otl-qty][data-otl-variant="' + cssEscape(carry.vid) + '"]');
          if (again) {
            if (carry.dirty) {
              again.value = carry.value;
              again.setAttribute('data-otl-dirty', '1');
            }
            again.focus();
            if (carry.start != null) {
              try { again.setSelectionRange(carry.start, carry.end); } catch (e) {}
            }
            liveRecountSafe();
          }
        } else if (!upgraded) {
          // Nothing was focused — the real grid has arrived, so offer it the caret.
          var first = list.querySelector('[data-otl-qty]');
          if (first) first.focus();
        }
      }).catch(function (err) { log('size grid upgrade failed', err); });
    });
  }

  /**
   * Group keys contain a colour, and 26 real colours contain a slash ("Black
   * Mesh/Black"); some contain quotes. Interpolating one straight into a selector
   * throws a SyntaxError and takes the whole refresh down with it.
   */
  function cssEscape(v) {
    var str = String(v == null ? '' : v);
    if (typeof CSS !== 'undefined' && CSS && typeof CSS.escape === 'function') return CSS.escape(str);
    return str.replace(/["\\\]\[#.:>+~*^$|()?{}]/g, '\\$&');
  }

  /**
   * Close the theme's cart drawer when our modal opens.
   *
   * WHY: the drawer auto-opens on every add (theme.liquid's fetch patch), so our
   * modal lands on top of it — and the drawer's contents are a SNAPSHOT from the
   * moment it opened. Adjust a stepper to 100 and the modal says "100 of 100" while
   * the drawer behind it still says 10. Two numbers for the same cart, on screen at
   * once, is precisely the confusion this is meant to remove.
   *
   * We click the theme's OWN close button first, so its internal state stays
   * consistent, and the drawer re-fetches /cart?view=_drawer whenever it opens so
   * it comes back correct.
   *
   * ⚠️ We then set the closed state ourselves as well (shutDrawer). That is NOT
   * belt-and-braces for its own sake: our syncThemeCartUi replaces the drawer's
   * innerHTML, which destroys the button the theme bound its handler to, so the
   * click alone can land on nothing. See hardenDrawerClose.
   */
  function closeCartDrawer() {
    try {
      if (!document.body.classList.contains('cart-drawer-open')) return;
      var close = document.querySelector('[data-cart-drawer-close]');
      if (close) close.click();
      // Belt and braces: if nothing is listening (see hardenDrawerClose), do it
      // ourselves rather than leaving the customer holding an open drawer.
      shutDrawer();
    } catch (err) { log('could not close drawer', err); }
  }

  /**
   * Close the drawer the FIRST time it appears within a short window — then stop.
   *
   * Used when closing the builder leaves cart work outstanding — a debounce still
   * counting, a write queued or running, or an /cart/add recent enough that
   * theme.liquid may still be about to open the drawer over the page the customer
   * just asked to return to. (It can therefore arm when no flush was needed at all;
   * that costs one bounded, close-once watcher, which is the cheap direction to be
   * wrong in.) Firing at fixed moments loses that race in BOTH directions:
   * closeCartDrawer() early-returns while the drawer has not opened YET, and if the
   * flush is slow it slams shut a drawer the customer opened on purpose. Watching
   * is bounded (~1.5s) and closes at most once, so anything they open after that is
   * left alone.
   */
  function closeDrawerIfItReopens() {
    // Shut it now if it is already open...
    closeCartDrawer();
    /**
     * ...and watch REGARDLESS. A `if (still open) return;` used to sit here, with a
     * comment claiming "already open? then we are done." It could never fire —
     * closeCartDrawer calls shutDrawer, which drops `cart-drawer-open`
     * unconditionally — so it was a dead guard describing behaviour the code cannot
     * produce, and had it ever fired it would have abandoned the watch in the one
     * case where closing had just FAILED. Watching always is both simpler and right.
     */
    var deadline = now() + 1500;
    var iv = setInterval(function () {
      if (document.body.classList.contains('cart-drawer-open')) {
        closeCartDrawer();
        clearInterval(iv);
        return;
      }
      if (now() > deadline) clearInterval(iv);
    }, 80);
  }

  var drawerClosingUntil = 0;

  /**
   * The state changes the theme's own close handler makes. Idempotent.
   *
   * Clearing `cart-drawer-open` is what actually closes it — but that class is also
   * what stops syncThemeCartUi rewriting the drawer's markup, so dropping it
   * synchronously re-arms that hazard while the panel is still animating shut.
   * `drawerClosingUntil` holds the sync off for the length of the animation.
   */
  function shutDrawer() {
    var drawer = document.querySelector('[data-cart-drawer]');
    var overlay = document.querySelector('[data-cart-overlay]');
    if (overlay) overlay.classList.remove('active');
    if (drawer) drawer.setAttribute('data-state', 'closed');
    document.body.classList.remove('cart-drawer-open');
    /**
     * Armed UNCONDITIONALLY. Gating on "was it open?" read false on the common
     * path: theme.liquid binds its close handler to the button in the TARGET
     * phase, so it has already cleared `cart-drawer-open` by the time this
     * document-level listener runs. The hold-off therefore never armed in exactly
     * the case it was written for. Arming when nothing was open costs a 500ms
     * pause on one drawer refresh — nothing.
     */
    drawerClosingUntil = now() + 500;
  }

  /**
   * ⚠️ WE BREAK THE THEME'S CLOSE BUTTON, so we have to replace it.
   *
   * theme.liquid binds its drawer-close handler to the BUTTON NODE
   * (`closeBtn.addEventListener`, guarded by `closeBtn.__otlBound`) and only ever
   * does so inside refreshAndOpenDrawer. Our syncThemeCartUi replaces
   * `drawerEl.innerHTML` to keep the drawer in step — which destroys that button
   * and mints a fresh, unbound one. From then until the next add, the X does
   * NOTHING: verified in a browser, the drawer sat open and clicking close had no
   * effect. (The overlay still closed it, because the overlay lives outside
   * [data-cart-drawer] and survives — so the customer was not trapped, just
   * confused.)
   *
   * Delegating from `document` is how everything else in this file survives the
   * drawer's DOM being replaced; the close button needs the same treatment. Safe
   * alongside the theme's own handler: both do the same idempotent thing.
   */
  function hardenDrawerClose() {
    document.addEventListener('click', function (e) {
      if (!e.target.closest) return;
      if (!e.target.closest('[data-cart-drawer-close]')) return;
      /**
       * The theme's own handler calls preventDefault, and this listener exists
       * precisely for when that handler is GONE — so nothing else is cancelling
       * the default. Retina's close control sits inside the drawer's
       * <form action="/cart" method="post">; a <button> without type="button", or
       * an <a href="#">, would otherwise submit or navigate as it closes.
       */
      e.preventDefault();
      shutDrawer();
    });
  }

  /**
   * True while the customer is typing into a stepper.
   *
   * FINDING E: refreshModal() replaces the size list's innerHTML, which destroys the
   * very input being typed into — and 450ms of debounce is shorter than it takes to
   * type "120", so the box was being pulled out from under them mid-number. The bar
   * is already kept current by liveRecount(), so skipping the re-render costs
   * nothing while a box has focus.
   */
  function modalInputFocused() {
    var el = document.activeElement;
    return Boolean(el && el.matches && el.matches('[data-otl-qty]') &&
      el.closest && el.closest('[data-otl-preorder-modal]'));
  }

  /** Re-render the modal in place from a fresh cart, preserving it being open. */
  function refreshModal() {
    var open = document.querySelector('[data-otl-preorder-modal]');
    if (!open) return Promise.resolve();
    return getCart().then(function (cart) {
      var groups = core.groupPreorderLines(cart.items, MINIMUM);
      // Keep every group that's still in the cart, so a group turning green is
      // visible feedback rather than the row vanishing under the customer.
      var shown = groups.filter(function (g) { return g.units > 0; });

      /**
       * FINDING D: do NOT close a builder the customer deliberately opened.
       *
       * "Start a 100pc run" opens the builder with nothing of that colour in the
       * cart, so `shown` is empty on the very first refresh and the panel closed
       * itself before they could add anything. The same happened whenever they
       * zeroed a size to redistribute it. Fall back to the groups we opened with,
       * emptied — the size grid is what they came for, not the count.
       */
      if (!shown.length) {
        if (!openedGroups.length) { closeModal(); return; }
        shown = openedGroups.map(function (g) {
          return {
            key: g.key, productId: g.productId, handle: g.handle,
            productTitle: g.productTitle, styleLabel: g.styleLabel, color: g.color,
            units: 0, url: g.url || null, lines: [], shortfall: MINIMUM,
          };
        });
      }

      // D1 — the heading tracks the real state. It used to read "Pre-order minimum
      // not met" while a green tick under it said "100 of 100 pieces", so the modal
      // contradicted itself at the exact moment the customer had fixed the problem.
      var title = open.querySelector('[data-otl-modal-title]');
      if (title) title.textContent = modalTitle(shown);

      // D2 — and the way forward opens up the moment nothing is short.
      var cont = open.querySelector('[data-otl-continue]');
      if (cont) {
        var short = shown.some(function (g) { return g.shortfall > 0; });
        cont.disabled = short;
        if (short) cont.setAttribute('aria-disabled', 'true');
        else cont.removeAttribute('aria-disabled');
      }

      // Everything above is a text/attribute update and is safe mid-type. Replacing
      // the list is not — see modalInputFocused.
      if (modalInputFocused()) return;

      var list = open.querySelector('[data-otl-group-list]');
      if (list) {
        list.innerHTML = shown.map(function (g) {
          var product = productCache[g.handle] || null;
          var html = groupSection(g, product, cart.items);
          // Rebuilt from cached product data IS the full grid, so mark it upgraded
          // — otherwise upgradeSections would treat the next pass as a first render
          // and steal focus back off the customer.
          return product ? html.replace('data-otl-group=', 'data-otl-upgraded="1" data-otl-group=') : html;
        }).join('');
      }
      upgradeSections(shown, cart.items || []);
    });
  }

  function esc(s) {
    return String(s == null ? '' : s)
      .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;').replace(/'/g, '&#39;');
  }

  /** Remove every line in a group. Sequential + keyed — never by line index. */
  function removeGroup(key) {
    return getCart().then(function (cart) {
      var groups = core.groupPreorderLines(cart.items, MINIMUM);
      var group = null;
      for (var i = 0; i < groups.length; i++) if (groups[i].key === key) group = groups[i];
      if (!group) return;

      // Line indexes shift as lines are removed, so we address lines by their
      // stable `key`, and we go one at a time — Shopify's cart is not safe to
      // mutate concurrently.
      return group.lines.reduce(function (chain, line) {
        return chain.then(function () {
          /**
           * ⚠️ Through setLineQuantity, NOT postCart directly — that is where the
           * `r.ok` check lives. Writing raw made a 422/5xx resolve as SUCCESS, so a
           * line stayed in the cart while we refreshed, closed the panel and reloaded
           * the cart page showing the run still there: the "remove from cart doesn't
           * remove the items" symptom this file already documents, by another route.
           *
           * A 404 is the one status that IS success here — the line is already gone.
           */
          return setLineQuantity(line.key, 0).catch(function (err) {
            if (err && err.otlStatus === 404) return null;
            throw err;
          });
        });
      }, Promise.resolve());
    });
  }

  // ── checkout gating ───────────────────────────────────────────────────────
  function now() {
    return (typeof performance !== 'undefined' && performance.now) ? performance.now() : Date.now();
  }

  var checking = false;
  var refreshQueued = false;
  var inflight = null;
  var readRetried = false;
  var lastCart = null;
  /**
   * Optimistically-claimed stock from an add that HAS committed but that `lastCart`
   * has not caught up with yet. The refresh after an add is scheduled at +300ms and
   * then needs its own round-trip, while the Add to Cart button re-arms the instant
   * the POST resolves — roughly a 400ms window in which a second add read a pre-add
   * snapshot and re-split against stock that was already spoken for, reproducing the
   * exact bug the cart-aware split fixes.
   *
   * Entries are dropped by a refresh whose read STARTED after them. That does NOT
   * make double counting impossible — a refresh already in flight when the add
   * commits can return a body that already includes it and still keep the claim. It
   * is harmless because of an invariant, not because of the filter: whenever
   * `split.preorder > 0` the claim equals the entire remaining stock, so it drives
   * remaining to 0, and doubling 0 is 0. An exhaustive sweep over stock × claimed ×
   * quantity found no claim leaving remaining > 0.
   *
   * ⚠️ That invariant is what makes this safe. If anyone ever calls
   * notePendingClaim with a PARTIAL amount, double counting becomes live and will
   * over-stamp.
   */
  var pendingClaims = [];
  var lastCartStale = false;
  var expressGeneration = 0;

  function isCheckoutTrigger(el) {
    if (!el || !el.closest) return false;
    return Boolean(el.closest('[data-cart-submit]'));
  }

  /** "View cart" inside the drawer must stay clickable — it isn't checkout. */
  function isViewCart(el) {
    var n = el && el.closest ? el.closest('[data-cart-submit]') : null;
    return Boolean(n && (n.classList.contains('cart-drawer__view-cart') || n.getAttribute('name') === 'view-cart'));
  }

  /**
   * Gate a checkout attempt. We must decide synchronously (we cannot await a
   * fetch inside a click and still cancel it), so we keep a cached verdict that
   * is refreshed on every cart change, and re-verify right after.
   */
  var lastBlocking = null; // null = unknown yet

  function onCheckoutAttempt(e) {
    try {
      var target = e.target;
      if (!isCheckoutTrigger(target) || isViewCart(target)) return;
      if (lastBlocking === null) return;      // unknown → fail open
      if (!lastBlocking.length) return;       // nothing short → allow

      e.preventDefault();
      e.stopPropagation();
      e.stopImmediatePropagation();
      showModal(lastBlocking, 'checkout');
      log('blocked checkout', lastBlocking);
    } catch (err) {
      log('gate error — failing open', err); // never trap the customer
    }
  }

  /** The drawer's checkout is a NATIVE submit; cancel the form event too. */
  function onFormSubmit(e) {
    try {
      var form = e.target;
      if (!form || !form.matches) return;
      if (!form.matches('form[action*="/cart"]')) return;
      /**
       * D14 — the drawer's "View cart" is a submit button inside the SAME
       * <form action="/cart">, so it reaches here even though onCheckoutAttempt
       * deliberately let it through. Blocking it trapped the customer in the
       * drawer: the one control that takes them somewhere they can actually fix
       * the problem was the one control that did nothing.
       *
       * `submitter` is undefined on older Safari; there we fall back to the
       * focused element, and failing that we gate (the checkout button is the
       * riskier miss of the two).
       */
      var submitter = e.submitter || document.activeElement;
      if (submitter && isViewCart(submitter)) return;
      if (lastBlocking === null || !lastBlocking.length) return;
      e.preventDefault();
      e.stopPropagation();
      e.stopImmediatePropagation();
      showModal(lastBlocking, 'checkout');
    } catch (err) {
      log('submit gate error — failing open', err);
    }
  }

  // ── cart surfaces: run bars + line labels ─────────────────────────────────
  /**
   * How long a made-to-order run takes, in the customer's words. Config so the
   * operator can change it without touching an asset.
   */
  var LEAD_TIME = cfg.leadTime || '~3 weeks';

  /**
   * The run bars on the CART PAGE.
   *
   * Replaces the old bare "Pre-order minimum not met" list. Every run in the cart
   * gets a bar — including the ones that are already full, because "Minimum met —
   * ready to check out" is the single most useful thing the page can tell someone
   * who has just spent five minutes building a run.
   *
   * It goes at the TOP of the cart form, full width. It first went next to the
   * checkout button — which sits in a 102px sidebar column, so the message wrapped
   * one word per line. Verified in a browser, not assumed.
   */
  function renderCartRuns(cart) {
    var existing = document.querySelector('[data-otl-preorder-notice]');
    if (existing && existing.parentNode) existing.parentNode.removeChild(existing);
    if (!onCartPage()) return;

    var groups = core.groupPreorderLines((cart && cart.items) || [], MINIMUM)
      .filter(function (g) { return g.units > 0; });
    if (!groups.length) return;

    // Cart PAGE only. The drawer is too cramped for the full block and its DOM is
    // replaced on every add; it gets the compact bar instead (renderDrawerRuns).
    var form = document.querySelector('#cart_form');
    if (!form || form.closest('[data-cart-drawer]')) return;

    var el = document.createElement('div');
    el.className = 'otl-preorder-runs';
    el.setAttribute('data-otl-preorder-notice', '');
    el.innerHTML = groups.map(function (g) { return core.runProgressHtml(g, MINIMUM); }).join('');
    form.insertBefore(el, form.firstChild);
  }

  /** The same bars, compact, inside the drawer. */
  function renderDrawerRuns(cart) {
    var drawer = document.querySelector('[data-cart-drawer]');
    if (!drawer) return;
    var old = drawer.querySelector('[data-otl-drawer-runs]');
    if (old && old.parentNode) old.parentNode.removeChild(old);

    var groups = core.groupPreorderLines((cart && cart.items) || [], MINIMUM)
      .filter(function (g) { return g.units > 0; });
    if (!groups.length) return;

    var el = document.createElement('div');
    el.className = 'otl-preorder-runs otl-preorder-runs--compact';
    el.setAttribute('data-otl-drawer-runs', '');
    el.innerHTML = groups.map(function (g) { return core.runProgressHtml(g, MINIMUM); }).join('');
    // Above the line items, where it reads as a summary rather than a footnote.
    drawer.insertBefore(el, drawer.firstChild);
  }

  /**
   * D10 — tell the two lines apart.
   *
   * After a split the cart holds two rows for the SAME variant at the SAME price
   * with the SAME title: 27 that ship now and 100 that do not. Nothing on screen
   * distinguishes them, so the customer reasonably reads it as a duplicate and
   * removes one.
   *
   * The Liquid path cannot do this — it cannot reach the drawer, whose markup is
   * injected by JS — so both surfaces are labelled from /cart.js here.
   *
   * ROW MATCHING is deliberately defensive. Retina's cart markup is not ours and
   * has changed between versions, so we try the key-bearing hooks first and fall
   * back to document order (which /cart.js matches). If nothing matches we label
   * nothing — an unlabelled cart is the status quo; a mislabelled one tells the
   * customer a lie about what ships when.
   */
  /**
   * The row for one cart line, on either surface.
   *
   * ⚠️ EVERY selector here was read off the LIVE Retina 6.3.0 DOM in a browser,
   * because the first version guessed and got all of it wrong: it looked for
   * `input[name="updates[<key>]"]` (Retina emits a bare `updates[]`), for
   * `[data-cart-item]` / `.cart__row` (neither exists), and fell back to document
   * order against a `.cart-item` count of THREE for a two-line cart — the third
   * being `.cart-item--titles`, the header. Net effect: the cart page got no
   * labels at all, which is precisely the bug D10 exists to fix.
   *
   * What Retina actually gives us is `data-line-id`, a 1-BASED INDEX into
   * cart.items, present on both the cart page and the drawer. That is a real
   * handle, so it is the primary path; document order is kept only as a last
   * resort and only when the row count matches exactly.
   */
  function rowForLine(scope, line, index, lineCount) {
    var ROW = '.cart-item, .cart-drawer__item, [data-cart-item]';

    /**
     * A line's own KEY is safe unconditionally — it identifies the line, not a
     * position, so a stale DOM simply fails to match.
     */
    var byKey = scope.querySelector('[data-cart-item-key="' + cssEscape(line.key) + '"]') ||
      scope.querySelector('[data-line-item-key="' + cssEscape(line.key) + '"]');
    // Normalise to the ROW, exactly as the index path does below: these attributes
    // may sit on a nested control (Retina puts `data-line-id` on the quantity
    // input). Returning the child would host the label inside it — which is how
    // the tags ended up invisible inside an image wrapper the first time.
    if (byKey) return (byKey.closest && byKey.closest(ROW)) || byKey;

    /**
     * `data-line-id` is a 1-based INDEX, so it is only trustworthy while the DOM
     * holds exactly the cart we are labelling from. Remove a line in the drawer and
     * our labels run ~300ms ahead of the re-render: every tag would shift by one and
     * a pre-order row would be stamped "Ships now" — the one failure the D10 header
     * says must never happen. Count first, then trust the index.
     */
    if (!rowCountMatches(scope, lineCount)) return null;

    var marker = scope.querySelector('[data-line-id="' + (index + 1) + '"]');
    if (marker) {
      var byId = marker.closest ? marker.closest(ROW) : null;
      if (byId) return byId;
    }

    /**
     * Positional fallback, and ONLY when the row count matches the cart we are
     * labelling from. `.cart-item--titles` is the header row, not a line — counting
     * it made this guard reject every cart, which by luck was the safe outcome.
     */
    return realRows(scope)[index] || null;
  }

  /** Line rows, excluding Retina's `.cart-item--titles` HEADER row. */
  function realRows(scope) {
    var rows = scope.querySelectorAll('.cart-item, .cart-drawer__item, [data-cart-item]');
    var real = [];
    for (var i = 0; i < rows.length; i++) {
      if (!rows[i].classList.contains('cart-item--titles')) real.push(rows[i]);
    }
    return real;
  }

  function rowCountMatches(scope, lineCount) {
    return realRows(scope).length === lineCount;
  }

  /**
   * Where inside the row the label belongs: with the TITLE, not the picture.
   *
   * The first version's last-resort selector was a bare `a`, and the first link in
   * a Retina row is the image link — so the tag rendered inside a zero-height image
   * wrapper and was invisible on screen while looking perfectly present in the DOM.
   */
  function labelHostFor(row) {
    return row.querySelector('.cart-info, .drawer-item__info, .cart__product-title, [data-cart-item-title]') || row;
  }

  function labelLines(cart) {
    var items = (cart && cart.items) || [];
    var scopes = [];
    var form = document.querySelector('#cart_form');
    if (form && !form.closest('[data-cart-drawer]')) scopes.push(form);
    var drawer = document.querySelector('[data-cart-drawer]');
    if (drawer) scopes.push(drawer);

    scopes.forEach(function (scope) {
      // Clear ours first so a removed/re-split line never keeps a stale label.
      var stale = scope.querySelectorAll('[data-otl-line-tag]');
      for (var i = 0; i < stale.length; i++) {
        if (stale[i].parentNode) stale[i].parentNode.removeChild(stale[i]);
      }
      // Only label once a split has actually happened. A cart of ordinary in-stock
      // lines does not need "Ships now" stamped on every row — that is noise, and
      // it is the label the customer already assumes.
      var hasPreorder = items.some(function (l) { return core.lineIsPreorder(l.properties); });
      if (!hasPreorder) return;

      items.forEach(function (line, index) {
        var row = rowForLine(scope, line, index, items.length);
        if (!row) return;
        var tag = document.createElement('span');
        tag.className = 'otl-line-tag' + (core.lineIsPreorder(line.properties) ? ' otl-line-tag--preorder' : '');
        tag.setAttribute('data-otl-line-tag', '');
        tag.textContent = core.lineLabel(line, LEAD_TIME);
        var host = labelHostFor(row);
        // Sit above "Remove" rather than below it, so the label reads as part of
        // the product description instead of trailing after the controls.
        var remove = host.querySelector('.cart-item__remove, [data-cart-item-remove]');
        if (remove) host.insertBefore(tag, remove);
        else host.appendChild(tag);
      });
    });
  }

  /**
   * D3/D4 — the theme's OWN cart UI, brought back into step.
   *
   * The header count and the drawer are painted by the theme, from a snapshot taken
   * when they last rendered. Every write the run builder makes goes straight to
   * Shopify, so without this the badge kept saying 28 while the drawer said 1 and
   * the server held 127 — three numbers for one cart. Refreshing on every cart read
   * (not just on modal writes) is both simpler and strictly more correct.
   *
   * Deliberately does NOT open the drawer — the theme's own hook does that on add,
   * and re-opening it under the run builder would put a stale snapshot on top of
   * the live one all over again.
   */
  var drawerSyncing = false;
  function syncThemeCartUi(cart) {
    try {
      var countEl = document.querySelector('[data-header-cart-count]');
      if (countEl && cart && typeof cart.item_count === 'number') countEl.textContent = cart.item_count;
    } catch (err) { log('badge sync failed', err); }

    var drawerEl = document.querySelector('[data-cart-drawer]');
    if (!drawerEl || drawerSyncing) return;
    /**
     * FINDING F: never replace the drawer's markup while it is OPEN.
     *
     * theme.liquid's own refreshAndOpenDrawer() refetches /cart?view=_drawer and
     * rewrites the same element on every add. Two writers with no coordination, and
     * ours could land mid-open — yanking the contents out from under a drawer the
     * customer is reading, or fighting the open animation. While it is closed there
     * is nothing to disturb, and it re-fetches on open anyway, so the badge above
     * (which is cheap and raceless) is the part that always runs.
     *
     * An exception was briefly made for "our modal is open", but the drawer is
     * display:none for exactly that window, so it bought a refresh nobody could see
     * at the cost of the hazard this guard exists for.
     */
    if (document.body.classList.contains('cart-drawer-open')) return;
    // ...and not while it is animating shut — see shutDrawer.
    if (now() < drawerClosingUntil) return;
    drawerSyncing = true;
    // `/cart?view=_drawer` deliberately does NOT match the fetch hook's
    // /cart/(add|change|update|clear) pattern, so this cannot feed itself.
    /**
     * Bounded too: `drawerSyncing` is only cleared in the trailing .then, so a stall
     * here latched it ON for the life of the page. Every later sync then
     * short-circuited and the theme's drawer sat on a pre-write snapshot while the
     * badge and the builder moved on — the "two numbers for one cart" state this
     * function exists to remove, produced by its own guard failing closed.
     */
    fetchBounded('/cart?view=_drawer', { credentials: 'same-origin' }, DRAWER_READ_TIMEOUT_MS)
      .then(function (res) { return res.ok ? res.text : null; })
      .then(function (html) {
        if (!html) return;
        /**
         * RE-CHECK before writing. The guards above ran before a fetch that takes
         * 50-300ms, and theme.liquid opens the drawer on every /cart/add — which
         * the builder issues up to twice per stepper write. Without this the
         * response lands and rewrites a drawer that is now OPEN: content yanked out
         * from under a reader, and the freshly-bound close button destroyed. That
         * is FINDING F, reached through the back door.
         */
        if (document.body.classList.contains('cart-drawer-open')) return;
        if (now() < drawerClosingUntil) return;
        var doc = new DOMParser().parseFromString(html, 'text/html');
        var tpl = doc.querySelector('template[data-html="content"]');
        var next = tpl ? tpl.innerHTML
          : (doc.querySelector('[data-cart-drawer]') || doc.querySelector('.cart-drawer') || {}).innerHTML;
        if (next != null) drawerEl.innerHTML = next;
      })
      .catch(function (err) { log('drawer sync failed', err); })
      .then(function () {
        drawerSyncing = false;
        // Re-decorate: we just replaced the markup our labels lived in.
        try { renderDrawerRuns(cart); labelLines(cart); } catch (err) { log('drawer redecorate failed', err); }
      });
  }

  /**
   * Flag blocked checkout buttons via a BODY class, not per-button classes.
   *
   * Per-button marking missed the drawer entirely: the drawer's markup is injected
   * after our first cart read and replaced on every add, so its button never got
   * the class. A body class + a CSS descendant selector styles whatever exists at
   * paint time, including DOM that appears later.
   *
   * Deliberately NOT `disabled`: a disabled control swallows the click, so if this
   * script's state were ever wrong the customer would be stranded with no way to
   * check out and no explanation. Clicking is how they learn why. The gate is what
   * actually stops it.
   */
  function markCheckoutButtons(blocking) {
    document.body.classList.toggle('otl-preorder-has-block', blocking.length > 0);
  }

  /**
   * Express checkout starts suppressed on the cart page (the loader stamps
   * `otl-express-pending` on <html> during head parsing) and is only released once we
   * KNOW the cart is clear. Deliberately not symmetric with the checkout button: that
   * one fails open because a customer stranded with no way to pay and no explanation
   * is the worse outcome, whereas express failing open is a silent one-click bypass
   * of the whole gate. So this is the one thing in this file that fails CLOSED.
   */
  function releaseExpress(allowed) {
    // Cart page only. The CSS is scoped to `body.cart`, so stamping <html> anywhere
    // else is inert today — but the loader deliberately stamps only on the cart
    // template, and having the runtime undo that guard store-wide is exactly the
    // latent site-wide-leak shape the first review caught. Keep both ends honest.
    if (!onCartPage()) return;
    var el = document.documentElement;
    if (allowed) el.classList.remove('otl-express-pending');
    else el.classList.add('otl-express-pending');
  }

  /**
   * QUEUED, not dropped. `checking` used to make a concurrent call a silent no-op
   * that was never rescheduled — and two paths routinely overlap: applyQty chains
   * `.then(refresh)` while the fetch hook independently schedules one at +300ms after
   * the same /cart/change. Whichever lost was discarded, leaving `lastBlocking` and
   * the body class pinned to a PRE-write verdict indefinitely, so the gate decided
   * from a stale cart. Re-running once at the end costs one extra read and cannot
   * loop: the flag is cleared before the recursive call.
   */
  function refresh() {
    // Queued callers get the IN-FLIGHT chain, not an instantly-resolved promise. That
    // chain re-runs the queued pass inside its own tail, so `promptIfShort` and
    // `applyQty` see a POST-write verdict instead of the stale one they used to read.
    if (checking) { refreshQueued = true; return inflight || Promise.resolve(); }
    checking = true;
    var startedAt = expressGeneration;
    var readStartedAt = now();
    inflight = getCart().then(function (cart) {
      lastCart = cart;
      lastCartStale = false;
      // Only drop claims this read could actually have seen.
      pendingClaims = pendingClaims.filter(function (c) { return c.at >= readStartedAt; });
      if (typeof window.OTLPreorderProduct === 'object' && window.OTLPreorderProduct
          && typeof window.OTLPreorderProduct.onCartChanged === 'function') {
        try { window.OTLPreorderProduct.onCartChanged(); } catch (e) { log('onCartChanged failed', e); }
      }
      lastBlocking = core.blockingGroups(cart.items, MINIMUM);
      // FIRST, before anything that could throw: this is the only verdict that can
      // release express, and a render error must not turn a known-clear cart into a
      // hidden one via the catch below.
      //
      // …but ONLY if no recheck happened while this read was in flight. A read that
      // started before a pageshow/visibility recheck carries a pre-recheck snapshot;
      // letting it release express would silently undo the synchronous close that
      // recheck() just performed, for the one round-trip until the queued re-run
      // corrects it. Closing is always allowed — only RELEASING needs to be current.
      if (startedAt === expressGeneration) releaseExpress(lastBlocking.length === 0);
      else releaseExpress(false);
      // Render errors must not disable the checkout gate, and must not reset the
      // retry budget: `readRetried = false` used to sit HERE, before these two calls,
      // so a deterministic render throw both consumed a retry and refilled it on the
      // next successful read — an unbounded /cart.js poll every 1.5s for the life of
      // the page, with lastBlocking pinned to null (checkout gate off).
      try {
        // Bars are drawn from the WHOLE cart, not just the blocking groups — a run
        // that is already full still deserves to say so.
        renderCartRuns(cart);
        renderDrawerRuns(cart);
        labelLines(cart);
        markCheckoutButtons(lastBlocking);
        syncThemeCartUi(cart);
      } catch (renderErr) {
        log('render failed — verdict kept, gate intact', renderErr);
      }
      readRetried = false;
      log('cart refreshed', lastBlocking.length + ' blocking group(s)');
    }).catch(function (err) {
      /**
       * lastCart is deliberately NOT cleared here.
       *
       * A previous round nulled it, reasoning that an unknown cart should stop
       * answering the split confidently. It does the opposite: claimedStockFor then
       * returns 0, which is not "unknown" — it is the most optimistic possible answer
       * and exactly the cart-blind value this whole change exists to remove. One
       * transient /cart.js failure restored the original bug in full: with 27 already
       * in the cart and stock 27, a 28-piece add went back to 54 unstamped / 1
       * stamped, and a 10-piece add stopped being intercepted at all (buildItems
       * returns null) so Retina added 10 unstamped and express was offered again.
       * The checkout gate is failing open in the same window, so nothing catches it.
       *
       * So keep it — but be precise about WHY, because the obvious justification is
       * false. A stale cart is a floor on claimed stock ONLY WHILE THE CART GROWS.
       * The moment it SHRINKS (another tab empties it, a removal whose refresh failed)
       * the snapshot becomes a CEILING and over-reports, which stamps in-stock pieces
       * as pre-order — and that is sticky: nothing re-splits a line, and the checkout
       * function trusts a stamp unconditionally, so both layers agree on the wrong
       * answer permanently.
       *
       * Keeping it is still the right call, because nulling is wrong on EVERY failed
       * read while the shrink case needs a shrink AND a window. The window itself is
       * closed elsewhere: `snapshotIsStale()` makes onAtcClick re-read before it
       * splits, so a suspect snapshot is never the basis of a decision.
       */
      lastCartStale = true;
      lastBlocking = null; // unknown → fail open for CHECKOUT…
      releaseExpress(false); // …but closed for EXPRESS (see releaseExpress)
      log('cart read failed — checkout fails open, express hidden, split keeps the STALE cart (stale=' + lastCartStale + ')', err);
      // ONE bounded retry. On a static /cart view refresh() runs exactly once (the
      // drawer can't be opened there and Retina's quantity control does a full form
      // submit), so without this a single 5xx or flaky-mobile blip removed express for
      // the entire page view on a perfectly good cart. Fail closed, but not forever.
      if (!readRetried) {
        readRetried = true;
        setTimeout(refresh, 1500);
      }
    }).then(function () {
      checking = false;
      if (refreshQueued) { refreshQueued = false; return refresh(); }
    });
    return inflight;
  }

  // ── wiring ────────────────────────────────────────────────────────────────
  function init() {
    hardenDrawerClose();

    // Capture phase: beats Retina's `Cart` bubble handler on the cart page.
    document.addEventListener('click', onCheckoutAttempt, true);
    // Native submit path: the drawer's checkout, and Enter-in-form anywhere.
    document.addEventListener('submit', onFormSubmit, true);

    // Delegated — the drawer's DOM is replaced on every add.
    document.addEventListener('click', function (e) {
      if (!e.target.closest) return;

      if (e.target.closest('[data-otl-preorder-close]')) { closeModal(); return; }

      var rm = e.target.closest('[data-otl-remove-group]');
      if (rm) {
        e.preventDefault();
        rm.disabled = true;
        removeGroup(rm.getAttribute('data-otl-remove-group'))
          .then(refresh)
          .then(function () {
            closeModal();
            // The cart PAGE renders its line items server-side, so the row stays on
            // screen until we reload.
            //
            // ⚠️ This used to test `querySelector('#cart_form') && !querySelector('[data-cart-drawer] #cart_form')`
            // — which is ALWAYS false, because the cart page contains BOTH its own
            // #cart_form AND the drawer's (duplicate IDs; there are literally two).
            // So it never reloaded, the removed line stayed visible, and the operator
            // correctly reported "remove from cart doesn't remove the items" — the
            // removal had actually worked, the screen just never caught up. Exactly
            // the duplicate-ID trap this file's own header warns about. Ask the URL,
            // not the DOM.
            if (onCartPage()) window.location.reload();
          })
          .catch(function (err) { log('remove failed', err); rm.disabled = false; });
      }
    });

    // ── steppers inside the run builder ──────────────────────────────────────
    /**
     * A row's stepper writes the number of MADE-TO-ORDER pieces of that size.
     *
     * Two shapes, because the builder shows sizes that are not in the cart yet:
     *   - an existing stamped line  → /cart/change.js by its stable `key`
     *   - a size with no line yet   → /cart/add.js with the shared stamp
     * Going to 0 on an existing line removes it, which is how a customer takes a
     * size back out of the run. (The old modal floored at 1 because every row WAS
     * a cart line and 0 made it vanish mid-edit; here the row is a size and stays
     * on screen at 0, so the floor is no longer needed — and without 0 there would
     * be no way to undo a size.)
     */
    /**
     * Write one size's TOTAL, split honestly into pieces that ship now and pieces
     * we make.
     *
     *   shipNow = min(total, on hand)   → an UNSTAMPED line; never counts toward the run
     *   made    = the rest              → a STAMPED line;    this is the run
     *
     * Both halves are written one after the other, never in parallel: Shopify's
     * cart is not safe to mutate concurrently, which is the same reason
     * removeGroup() removes its lines one at a time.
     *
     * Going to 0 clears both lines, which is how a customer takes a size back out
     * of the run. (The old modal floored at 1 because every row WAS a cart line and
     * 0 made it vanish mid-edit; here the row is a size and stays on screen at 0.)
     */
    function writeQty(input, total) {
      if (input.hasAttribute('readonly')) {
        // Degraded panel — stock unknown. See rowsFor.
        log('refusing to write a size whose stock we could not read');
        return Promise.resolve();
      }
      var variantId = input.getAttribute('data-otl-variant');
      var available = parseInt(input.getAttribute('data-otl-avail'), 10);
      if (isNaN(available) || available < 0) available = 0;
      var split = core.splitRunTotal(total, available);

      // The stamp comes off the ROW, not out of the cart — see sizeRow. A run
      // started from the product page has nothing in the cart to look up.
      var color = input.getAttribute('data-otl-color');
      var props = core.preorderProperties(
        input.getAttribute('data-otl-style') || null,
        color ? color : null
      );

      function put(keyAttr, quantity, properties) {
        var key = input.getAttribute(keyAttr);
        if (key) {
          return setLineQuantity(key, quantity).then(function (r) {
            // A line taken to 0 is gone and its key addresses nothing. Forget it,
            // or the next edit would change a line that no longer exists.
            if (quantity <= 0) input.removeAttribute(keyAttr);
            return r;
          }, function (err) {
            /**
             * ⚠️ ONLY a 404 may fall through to an add.
             *
             * A 404 means the line is genuinely gone (removed in the drawer or
             * another tab), so re-adding restores the customer's number. Every
             * other failure means the line probably still EXISTS: a 429 (this file
             * already documents Shopify throttling Ajax cart calls, and each
             * stepper write issues two of them), a 5xx, or a dropped connection
             * where the change may well have committed server-side. Adding on top
             * of a line that is still there makes Shopify SUM the quantities — the
             * customer sets a size to 60 and gets 100. That is the exact double-add
             * the key adoption exists to prevent, reached by the recovery path.
             *
             * So: heal the one case we can identify, and let the rest reject to
             * flushRow's catch, which logs and leaves the cart alone.
             */
            if (err && err.otlStatus === 404) {
              input.removeAttribute(keyAttr);
              if (quantity <= 0) return null;
              log('line was gone — re-adding', err);
              return add(keyAttr, quantity, properties);
            }
            throw err;
          });
        }
        if (quantity <= 0) return Promise.resolve(); // nothing there, nothing to remove
        return add(keyAttr, quantity, properties);
      }

      function add(keyAttr, quantity, properties) {
        var body = { id: variantId, quantity: quantity };
        if (properties) body.properties = properties;
        return postCart('/cart/add.js', body).then(function (res) {
          if (!res.ok) {
            throw new Error((res.body && res.body.description) || res.statusText || ('cart/add ' + res.status));
          }
          /**
           * ⚠️ Stamp the moment the add LANDED — and stamp it here, before the
           * parse check below can throw.
           *
           * theme.liquid opens the drawer on `res.ok` alone and never looks at the
           * body, so an ok-but-unreadable response still re-opens the drawer. If the
           * stamp sat behind the parse check, the one case that throws would also be
           * the one case the drawer chase went blind to.
           *
           * This — not our write settling — is the event theme.liquid answers with
           * `setTimeout(refreshAndOpenDrawer, 250)`. A `/cart/change` never reaches
           * here, and never re-opens the drawer.
           */
          lastAddResolvedAt = now();
          /**
           * ⚠️⚠️ AN UNREADABLE 200 MUST REJECT. The add body is the ONLY carrier of
           * the new line key, so swallowing it into `null` reported success with no
           * key adopted — and this file's whole key-adoption apparatus exists
           * because a missing key sends the next "+" down the add path, where
           * Shopify MERGES adds carrying identical properties by SUMMING them.
           * A lapsed wholesale session or a bot-check interstitial answers
           * /cart/add.js with 200 HTML, so this is a real response, not a
           * hypothetical one. Rejecting routes into the resync, which re-reads the
           * cart and adopts the true key.
           */
          if (!res.parsed) throw new Error('cart/add: unreadable body — no line key');
          return res.body;
        }).then(function (line) {
          /**
           * Adopt the new line's key immediately.
           *
           * Without this the row still says "no line yet", so the customer's NEXT
           * change to this size takes the add path again — and Shopify MERGES adds
           * carrying identical properties by SUMMING them, so setting 40 and then
           * 60 would land 100 instead of 60.
           */
          /**
           * ⚠️ …ONTO THE LIVE ROW, not the node we started with.
           *
           * `input` was resolved before the fetch. upgradeSections replaces the size
           * grid's innerHTML on the first product upgrade UNCONDITIONALLY (that is
           * deliberate — the focus guard is skipped on the first pass or the builder
           * never grows past the size already in the cart), so on a slow product
           * fetch the node we hold is detached by the time the add lands. The key
           * then went to an orphan, the live row still said "no line yet", and the
           * next "+" took the add path again — Shopify SUMS adds carrying identical
           * properties, so 120 became 220. And the repair that would normally rescue
           * it does not run: upgradeSections focuses a box, and refreshModal bails
           * while a box has focus.
           */
          var target = liveInputFor(variantId) || input;
          if (line && line.key) target.setAttribute(keyAttr, line.key);
          return line;
        });
      }

      // Ships-now first: it only ever shrinks or holds when `made` grows, so the
      // cart passes through no state where the customer appears to owe more than
      // they asked for.
      return put('data-otl-stock-key', split.shipNow, null)
        .then(function () { return put('data-otl-pre-key', split.made, props); });
    }

    /**
     * ⚠️ ONE TIMER PER SIZE, and cart writes SERIALISED.
     *
     * The first version kept a single `pending` timer for the whole builder and
     * called clearTimeout() on every keystroke. With one row that is a debounce;
     * with seven rows it is a bug — stepping S then M within 450ms CANCELLED S's
     * write outright. The optimistic bar still counted S, so the customer watched
     * it reach 100 while the cart held 60 and got bounced at checkout. That is the
     * normal way a run builder gets used, not an edge case.
     *
     * Writes are serialised through one chain because Shopify's cart is not safe to
     * mutate concurrently — the same reason removeGroup() removes lines one at a
     * time rather than in parallel.
     */
    var pendingTimers = {};   // variantId -> timeout handle
    var pendingArgs = {};     // variantId -> the latest {input, value} for that size
    var writeChain = Promise.resolve();
    /**
     * ⚠️ A DEBOUNCE TIMER IS NOT THE ONLY KIND OF PENDING WORK — and neither is a
     * write that has STARTED.
     *
     * flushRow deletes pendingTimers[vid] the instant the 450ms debounce fires, so
     * "are there timers?" is already blind to a running write. Round 7 counted the
     * running write — but counted it INSIDE the enqueued function, so a write still
     * QUEUED behind another one on `writeChain` had no timer, no count, and the
     * previous write's grace period ticking down. Step S then M with a slow
     * connection and M's /cart/add had not even been issued when the customer closed
     * the panel. Count at ENQUEUE time; only then does the counter mean "work the
     * customer is owed".
     *
     * ⚠️⚠️ And key the grace period to the ADD, not to the write.
     *
     * Only `/cart/add` re-opens the drawer — theme.liquid's patch matches that path
     * alone, so every `/cart/change` write is irrelevant here. It answers a
     * successful add with `setTimeout(refreshAndOpenDrawer, 250)`, and that function
     * then fetches /cart?view=_drawer AND /cart.js before it opens. So the drawer
     * appears at add + 250ms + two round-trips — which on a slow connection is later
     * than a grace period measured from when our whole write settled. Measured from
     * the ADD, with room for both round-trips, it cannot expire early. Being
     * generous is the safe direction: a false "yes" costs one bounded, close-once
     * watcher, while a false "no" is the drawer ambushing the customer.
     */
    var outstandingWrites = 0;
    var lastAddResolvedAt = -Infinity;   // NOT 0 — now() is performance.now(), whose
                                         // origin is page load, so 0 reads as "just now"
                                         // for the first second of every page view.
    var DRAWER_REOPEN_WINDOW_MS = 3000;

    function rowId(input) {
      return String(input.getAttribute('data-otl-variant') || 'x');
    }

    /** Serialise onto the chain, and keep going after a failure. */
    function enqueue(fn) {
      writeChain = writeChain.then(fn, fn);
      return writeChain;
    }

    function flushRow(vid) {
      if (pendingTimers[vid]) { clearTimeout(pendingTimers[vid]); delete pendingTimers[vid]; }
      var a = pendingArgs[vid];
      if (!a) return Promise.resolve();
      delete pendingArgs[vid];

      // Owed from HERE — the write is queued, even if the chain has not reached it.
      outstandingWrites += 1;
      var released = false;
      function releaseWrite() { if (!released) { released = true; outstandingWrites -= 1; } }

      /**
       * ⚠️ Capture THE PANEL ITSELF at enqueue time, and later ask whether that
       * exact panel is still on the page.
       *
       * A boolean is not enough, and I got this wrong in both directions:
       *  - reading `is a panel open?` inside the queued function pointed at
       *    whatever panel existed by then. showModal() calls closeModal() (which
       *    enqueues this flush) and then synchronously appends a NEW panel, so a
       *    row missing from that new panel had its write dropped;
       *  - capturing the boolean at enqueue time was worse: closeModal flushes
       *    BEFORE it removes the modal, so the flag was always true on the ordinary
       *    close path and the captured-node fallback — the entire reason it exists
       *    — never ran. Verified live: the customer's last change silently vanished.
       *
       * The question that actually matters is "is the panel this row belonged to
       * still here?". If it is, a missing row means the row is genuinely gone (a
       * group filtered out of a rebuild) and writing through a detached node would
       * use pre-rebuild line keys. If it is not, we are finishing that panel's last
       * change and the captured node is exactly right.
       */
      var panelAtEnqueue = document.querySelector('[data-otl-preorder-modal]');
      var panelStillUp = function () {
        return !!(panelAtEnqueue && (panelAtEnqueue.isConnected !== undefined
          ? panelAtEnqueue.isConnected
          : document.contains(panelAtEnqueue)));
      };

      return enqueue(function () {
        // Resolve the row NOW, not when the click happened — the grid may have been
        // rebuilt in between and the old node detached.
        /**
         * Prefer the LIVE row — the grid may have been rebuilt since the tap.
         *
         * The captured node is a fallback for ONE case: the panel has closed and a
         * flush is finishing the customer's last change. Using it whenever the live
         * row is missing was wrong twice over — it made the "row is gone" guard
         * below unreachable, and with several runs open a group whose units drop to
         * 0 is filtered out of the re-render, so a still-pending write for one of
         * its sizes would execute against a detached node carrying pre-rebuild line
         * keys. A stale key 404s, and the heal path then RE-ADDS the line.
         */
        var input = liveInputFor(a.vid) || (panelStillUp() ? null : a.input);
        if (!input) {
          log('row for variant ' + a.vid + ' is gone; skipping its write');
          releaseWrite();
          return null;
        }
        // Promise.resolve() first so a SYNCHRONOUS throw out of writeQty
        // (splitRunTotal, preorderProperties) still reaches releaseWrite below.
        return Promise.resolve()
          .then(function () { return writeQty(input, a.value); })
          .then(function (r) { releaseWrite(); return r; },
                function (e) { releaseWrite(); throw e; })
          .then(refresh)
          .then(refreshModal)
          .then(function () {
            /**
             * Our own /cart/add.js is answered by theme.liquid's fetch patch, which
             * OPENS the drawer — underneath the builder. syncThemeCartUi then
             * refuses to touch an open drawer, so it sat there showing a snapshot
             * from before the write: two numbers for the same cart on screen at
             * once, the exact thing closeCartDrawer() exists to prevent (and it
             * only ran once, when the modal opened).
             */
            if (document.querySelector('[data-otl-preorder-modal]')) closeCartDrawer();
            // If nothing is short any more, the cart page's own totals are stale.
            if (onCartPage() && lastBlocking && !lastBlocking.length) window.location.reload();
          })
          .catch(function (err) {
            /**
             * ⚠️ RESYNC AFTER A FAILURE, or the builder keeps showing a number the
             * cart never got.
             *
             * A non-404 write (a 429 — and each stepper write issues up to two cart
             * calls — a 5xx, a dropped connection) now rejects rather than being
             * papered over. But the bare catch skipped refresh AND refreshModal, so
             * the row kept `data-otl-dirty` and its optimistic value: liveRecount
             * went on counting it, the bar still read 100/100, and "Continue to
             * checkout" stayed lit over a cart that was short. The customer's only
             * clue was the Continue bounce, with the number silently changing under
             * them.
             *
             * Dropping the dirty flag hands the row back to the cart's own count
             * (see liveRecount), and the re-render puts the true number on screen.
             */
            log('qty change failed — resyncing the panel to the cart', err);

            /**
             * ⚠️ FIRST, SYNCHRONOUSLY: drop the dirty flag and recount.
             *
             * Round 6 moved the recount BEHIND refresh(), so between the failure and
             * the cart read the bar still showed the optimistic 100/100 and
             * "Continue to checkout" stayed lit over a cart that was short — the
             * exact symptom the note above says must never happen. getCart() waits up
             * to CART_READ_TIMEOUT_MS (8s), so that false "you're done" could sit on
             * screen for eight seconds.
             *
             * Dropping the dirty flag hands the row back to the cart's own count, so
             * the bar tells the truth immediately. The box keeps the customer's
             * number until we know better — a value we cannot verify is not an
             * improvement on the one they typed.
             */
            function newerEditQueued() {
              return !!(pendingTimers[a.vid] || pendingArgs[a.vid]);
            }
            var early = liveInputFor(a.vid);
            // Not while a newer edit is waiting — the box holds THEIR number, and
            // clearing the flag would make the bar ignore it and dip for no reason.
            if (early && !newerEditQueued()) early.removeAttribute('data-otl-dirty');
            liveRecountSafe();

            /**
             * ⚠️ Resync from the CART, never from `defaultValue`.
             *
             * `defaultValue` is the `value` ATTRIBUTE sizeRow rendered, and nothing
             * updates it in place — only a full re-render does, and refreshModal
             * deliberately bails while a box has focus. So it can be many successful
             * writes out of date, and restoring it is actively destructive: open at
             * M = 10, type 100 (writes fine, no re-render because focused), type 120
             * (429s) → the box would snap back to 10 against a cart holding 100, and
             * one tap of "+" would write 20 and destroy 80 real pieces.
             *
             * The only trustworthy number is the one the cart just gave us, so read
             * it AFTER refresh() and put that on the row.
             */
            return refresh().then(function () {
              /**
               * ⚠️ THE SAME FALLBACK THE WRITE PATH USES. `liveInputFor` returns null
               * the moment the modal leaves the DOM — and closing the panel is a
               * normal way to finish an edit, so `if (!row) return` silently skipped
               * the key repair on exactly that path. The write path already knew
               * better (see the `panelStillUp()` line above); the repair path did
               * not, which is how the repair could be called "unconditional" and
               * still not run.
               *
               * It matters because the captured node IS the node a queued write will
               * read: tap "+", let the add commit but the response fail, tap "+"
               * again, press Escape — closeModal flushes that second write behind
               * this resync, the resync repaired nothing, and the queued write went
               * down the add path with no key. Ask for 130, get 250.
               */
              var row = liveInputFor(a.vid) || (panelStillUp() ? null : a.input);
              if (!row) return;

              /**
               * ⚠️⚠️ refresh() RESOLVES EVEN WHEN THE CART READ FAILED.
               *
               * Its catch swallows the error, sets `lastCartStale` and deliberately
               * KEEPS the previous `lastCart` — so "the number the cart just gave us"
               * can be a pre-write snapshot, and this whole block would then write a
               * stale total, stale pre-units and stale line keys onto the row. That
               * is the destructive reset this fix exists to remove, arriving through
               * a different door.
               *
               * It is not hypothetical: each stepper write issues TWO cart calls. If
               * the ships-now add() succeeds (adopting a fresh stock key) and the
               * pre-order call is throttled, /cart.js is very likely throttled too —
               * stockLineFor would then return null against the stale snapshot and we
               * would DELETE the key of a line that was just created. The next edit
               * takes the add path, and Shopify SUMS adds carrying identical
               * properties: the double-add the key adoption exists to prevent.
               *
               * When the cart is unknown, write nothing. refreshModal below does its
               * own read, and a later successful refresh re-renders the true numbers.
               *
               * ⚠️ KNOWN, ACCEPTED GAP — and it is now the ONLY one. This bail also
               * skips the key repair below, because a stale snapshot's keys are
               * exactly what must not be written. So if an add COMMITTED, then
               * failed, AND the cart is unreadable, AND a newer edit is queued, that
               * edit takes the add path and Shopify sums it. Every layer of that
               * conjunction has to fail at once and there is no source of truth left
               * to repair from. (The sibling case — same thing but with the PANEL
               * CLOSED rather than the cart unreadable — was a real hole too and is
               * closed: the repair now falls back to the captured node.) It is a
               * genuine hole, not a covered case. Do not let a later comment claim
               * otherwise.
               */
              if (lastCartStale) {
                log('cart could not be re-read — leaving the row alone rather than writing a guess');
                return;
              }

              var items = (lastCart && lastCart.items) || [];
              var pre = core.preorderLineFor(items, a.vid);
              var stock = core.stockLineFor(items, a.vid);
              var preUnits = pre ? parseInt(pre.quantity, 10) || 0 : 0;

              /**
               * ⚠️⚠️ REPAIR IDENTITY UNCONDITIONALLY — the line keys are not the
               * customer's number, and a queued write needs them more than we do.
               *
               * This block used to sit behind the "a newer edit is queued" bail
               * below, which protected the NUMBER but also skipped the keys. A write
               * that failed AFTER committing then left the row with no
               * `data-otl-pre-key` — so the queued write took the add path, and
               * Shopify SUMS adds carrying identical properties. Ask for 120, get
               * 220. Now reachable through the 12s abort too: an add that commits on
               * a dying connection rejects with no key ever adopted.
               *
               * Keys may also have moved if the failure was a partial write.
               */
              row.setAttribute('data-otl-pre-units', preUnits);
              if (pre) row.setAttribute('data-otl-pre-key', pre.key);
              else row.removeAttribute('data-otl-pre-key');
              if (stock) row.setAttribute('data-otl-stock-key', stock.key);
              else row.removeAttribute('data-otl-stock-key');

              /**
               * ⚠️ …but do not clobber a NEWER edit's NUMBER. The failure path costs
               * a full round-trip; if the customer kept tapping "+" meanwhile, their
               * number is queued in pendingArgs. Overwriting the box with the cart
               * figure and then letting that queued write land leaves the box reading
               * 100 while the cart holds 150 — and refreshModal bails while the box
               * has focus, so nothing corrects it. The next "+" then steps from 100.
               */
              if (newerEditQueued()) {
                log('a newer edit for this size is already queued — keys repaired, number left to it');
                return;
              }

              row.removeAttribute('data-otl-dirty');
              var total = preUnits + (stock ? parseInt(stock.quantity, 10) || 0 : 0);
              var hadFocus = document.activeElement === row;
              row.value = total;
              if (hadFocus) { try { row.select(); } catch (e) {} }
            })
            /**
             * refreshModal BAILS OUT while a box has focus (it must not replace the
             * grid mid-type), so on the failure path it can re-render nothing at all
             * — leaving the bar showing a number the write never achieved. Recount
             * straight from the boxes.
             *
             * Note this recount is the SECOND one: the eager call above has already
             * told the truth about the bar. This one picks up whatever the resync
             * managed to put on the row — nothing at all on the stale-cart bail, and
             * the repaired KEYS but not the number on the newer-edit bail.
             */
            .then(refreshModal).then(liveRecountSafe).catch(function (e2) {
              log('resync after a failed write also failed', e2);
              liveRecountSafe();
            });
          });
      });
    }

    /** Fire every waiting write NOW and resolve once the chain has drained. */
    function flushAllRows() {
      Object.keys(pendingTimers).forEach(flushRow);
      return writeChain.catch(function () {});
    }

    /**
     * Re-resolve a row's input by VARIANT, never by a held node reference.
     *
     * refreshModal()/upgradeSections() replace the size list's innerHTML while a
     * debounce is still pending, so the node captured at click time is detached by
     * the time the write runs. The key adopted from /cart/add.js was then written
     * to that orphan, leaving the live row with no key — so the customer's next tap
     * took the ADD path again and Shopify SUMMED the quantities. That is precisely
     * the double-add the key-adoption code exists to prevent.
     */
    function liveInputFor(vid) {
      var modal = document.querySelector('[data-otl-preorder-modal]');
      if (!modal) return null;
      return modal.querySelector('[data-otl-qty][data-otl-variant="' + cssEscape(vid) + '"]');
    }

    function applyQty(input, value) {
      var vid = rowId(input);
      if (pendingTimers[vid]) clearTimeout(pendingTimers[vid]);
      // Keep the node too: once the modal is gone liveInputFor() finds nothing, and
      // a flush triggered BY closing still has to write. The detached node retains
      // every attribute writeQty reads.
      pendingArgs[vid] = { vid: vid, value: value, input: input };
      // Debounce PER SIZE: tapping "+" ten times on one size is one write; touching
      // three sizes is three writes, not two lost ones.
      pendingTimers[vid] = setTimeout(function () { flushRow(vid); }, 450);
    }

    /**
     * D11 — an empty box, a lone "-" or anything non-numeric means "the customer is
     * mid-typing", NOT "set this size to zero". parseInt('') is NaN, which used to
     * be posted straight to /cart/change.js: clearing the box to retype 120 removed
     * the line under them.
     */
    function readQty(raw) {
      var str = String(raw == null ? '' : raw).trim();
      if (str === '') return null;
      if (!/^\d+$/.test(str)) return null;
      var n = parseInt(str, 10);
      if (isNaN(n) || n < 0) return null;
      return n;
    }

    function rowInput(el) {
      var row = el.closest ? el.closest('[data-otl-size-row]') : null;
      return row ? row.querySelector('[data-otl-qty]') : null;
    }

    document.addEventListener('click', function (e) {
      var step = e.target.closest && e.target.closest('[data-otl-step]');
      if (!step) return;
      e.preventDefault();
      var input = rowInput(step);
      if (!input) return;
      var current = readQty(input.value);
      if (current === null) current = 0; // stepping from a half-typed box starts at 0
      var next = Math.max(0, current + parseInt(step.getAttribute('data-otl-step'), 10));
      input.value = next;                 // optimistic — the number moves instantly
      input.setAttribute('data-otl-dirty', '1');
      liveRecount();
      applyQty(input, next);
    });

    document.addEventListener('input', function (e) {
      if (!e.target.matches || !e.target.matches('[data-otl-qty]')) return;
      e.target.setAttribute('data-otl-dirty', '1'); // theirs now — see upgradeSections
      var n = readQty(e.target.value);
      liveRecount();
      if (n === null) return;             // mid-typing — leave the cart alone
      applyQty(e.target, n);
    });

    // Publish the builder's internals to the module-level safe wrappers.
    liveRecountSafe = liveRecount;
    flushPendingSafe = flushAllRows;
    pendingWorkExists = function () {
      return Object.keys(pendingTimers).length > 0
        || outstandingWrites > 0
        || (now() - lastAddResolvedAt) < DRAWER_REOPEN_WINDOW_MS;
    };

    /**
     * Update the progress bar from the inputs alone, before the cart round-trip
     * lands. Without this the number lags ~half a second behind the click and the
     * builder feels broken.
     *
     * ⚠️ It must preview the SPLIT, not the stepper totals. The steppers set how
     * many pieces of each size the customer wants; only the part BEYOND stock is
     * made to order, and only that part counts toward the run. Summing the raw
     * totals here would show a bar that fills faster than the cart ever will, and
     * the customer would watch it hit 100 and then be told they were short.
     *
     * ⚠️⚠️ ...but ONLY for rows the customer has actually touched. An untouched row
     * keeps the cart's own count.
     *
     * Found with a two-style cart: a line stamped pre-order when the size was out
     * of stock stays stamped after you restock it (nothing re-splits an existing
     * line, and the checkout side trusts a stamp unconditionally). Re-deriving that
     * row from today's stock made the builder read 0/100 while the cart, the
     * cart-page bar and the gate all said 20 — four numbers for one run, which is
     * the exact class of contradiction this phase exists to remove. The cart is the
     * truth for what is already in it; the split is a preview of what a NEW edit
     * will do.
     */
    function liveRecount() {
      var modal = document.querySelector('[data-otl-preorder-modal]');
      if (!modal) return;
      var items = modal.querySelectorAll('.otl-preorder-modal__item');
      var anyShort = false;
      for (var i = 0; i < items.length; i++) {
        var item = items[i];
        var made = 0;
        var qtys = item.querySelectorAll('[data-otl-qty]');
        for (var j = 0; j < qtys.length; j++) {
          var box = qtys[j];
          if (box.getAttribute('data-otl-dirty') !== '1') {
            // Untouched — the cart's verdict stands.
            var known = parseInt(box.getAttribute('data-otl-pre-units'), 10);
            made += isNaN(known) || known < 0 ? 0 : known;
            continue;
          }
          var n = readQty(box.value);
          if (n === null) n = 0;
          var avail = parseInt(box.getAttribute('data-otl-avail'), 10);
          if (isNaN(avail) || avail < 0) avail = 0;
          made += core.splitRunTotal(n, avail).made;
        }
        var p = core.runProgress({ units: made }, MINIMUM);
        if (!p.met) anyShort = true;

        var run = item.querySelector('[data-otl-run]');
        var count = item.querySelector('[data-otl-run-count]');
        var fill = item.querySelector('[data-otl-run-fill]');
        var status = item.querySelector('.otl-run__status');
        if (count) count.textContent = p.units + ' / ' + p.minimum + ' pc';
        if (fill) fill.style.width = p.percent + '%';
        if (run) {
          run.classList.toggle('is-met', p.met);
          var track = run.querySelector('.otl-run__track');
          if (track) track.setAttribute('aria-valuenow', p.units);
        }
        if (status) status.textContent = core.runStatusMessage({ units: made }, MINIMUM);
        item.classList.toggle('is-ready', p.met);
      }

      // D2 — the way forward opens the instant the last run fills, without waiting
      // for the cart round-trip.
      var cont = modal.querySelector('[data-otl-continue]');
      if (cont) {
        cont.disabled = anyShort;
        if (anyShort) cont.setAttribute('aria-disabled', 'true');
        else cont.removeAttribute('aria-disabled');
      }
    }

    /**
     * D2 — "Continue to checkout" from inside the builder.
     *
     * Waits for any in-flight stepper write before leaving, or the customer departs
     * on an optimistic preview and Shopify's cart is a debounce behind them. Then it
     * re-reads the cart and only goes if the verdict is genuinely clear — the button
     * is enabled from the optimistic preview, which can be higher than the real
     * pre-order count when a stepped size still had stock.
     */
    document.addEventListener('click', function (e) {
      var go = e.target.closest && e.target.closest('[data-otl-continue]');
      if (!go || go.disabled) return;
      e.preventDefault();
      go.disabled = true;
      /**
       * FLUSH, never cancel. This used to clearTimeout() the waiting write and then
       * await the PREVIOUS one — so tapping "+" to reach 100 and clicking Continue
       * straight away threw away the very change that filled the bar, and the
       * customer was told they were still short by the screen that had just told
       * them they were done.
       */
      flushAllRows()
        .then(refresh)
        .then(function () {
          /**
           * `lastBlocking === null` means the cart read FAILED — unknown, not clear.
           * Testing only `lastBlocking.length` let a failed /cart.js read walk the
           * customer straight past the 100pc gate to checkout, which is the one
           * outcome this button must never produce. The gate elsewhere fails OPEN
           * deliberately (a stranded customer is worse than an under-minimum cart);
           * here we are the ones doing the navigating, so we require a real verdict.
           */
          if (!lastBlocking || lastBlocking.length) {
            go.disabled = false;
            return refreshModal();
          }
          closeModal();
          window.location.href = '/checkout';
        })
        .catch(function (err) {
          log('continue failed — sending them to the cart rather than stranding them', err);
          go.disabled = false;
        });
    });

    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape') closeModal();
    });

    // Re-read after anything that could change the cart. We wrap fetch rather
    // than guess: theme.liquid already wraps it for the drawer auto-open, and
    // wrapping is composable — we call through to whatever is there.
    var origFetch = window.fetch;
    if (typeof origFetch === 'function' && !window.__otlPreorderCartHook) {
      window.__otlPreorderCartHook = true;
      window.fetch = function () {
        var url = arguments[0];
        if (url && url.url) url = url.url;
        var p = origFetch.apply(this, arguments);
        if (typeof url === 'string' && /\/cart\/(add|change|update|clear)(\.js)?(\?|$)/.test(url)) {
          p.then(function () { setTimeout(refresh, 300); }).catch(function () {});
        }
        return p;
      };
    }

    /**
     * A cart page can be RESTORED rather than loaded — bfcache back-navigation, or
     * simply a second tab left open on /cart while the first one changes the cart. In
     * both cases no script re-runs, so a page that released express while the cart was
     * complete keeps it live after the cart becomes blocking again. One click.
     *
     * `releaseExpress(false)` fires SYNCHRONOUSLY first and the verdict is re-earned
     * afterwards: re-checking alone would leave the button live for the whole fetch.
     */
    /**
     * Cart page only, and the FETCH is rate-limited while the close is not.
     *
     * `visibilitychange` fires on every tab switch — and on mobile, on every app
     * switch and screen unlock. Registering this store-wide would turn one /cart.js
     * read per page load into one per visibility transition across the whole site,
     * and Shopify throttles the Ajax API: a burst of 430s would fail express closed on
     * a perfectly good cart, and could get a real /cart/add.js rejected. So the
     * synchronous fail-closed always runs (it costs nothing and is the safety
     * property), while the verification read coalesces to at most one per 2s.
     */
    var RECHECK_MIN_MS = 2000;
    var lastRecheckAt = 0;
    var recheckTimer = null;

    function recheck() {
      releaseExpress(false);
      expressGeneration += 1; // invalidate any read that started before this close
      var now = Date.now();
      var since = now - lastRecheckAt;
      if (since >= RECHECK_MIN_MS) {
        lastRecheckAt = now;
        refresh();
      } else if (!recheckTimer) {
        recheckTimer = setTimeout(function () {
          recheckTimer = null;
          lastRecheckAt = Date.now();
          refresh();
        }, RECHECK_MIN_MS - since);
      }
    }

    /**
     * Registered STORE-WIDE, not cart-page only.
     *
     * An earlier round scoped these to /cart to avoid a /cart.js read on every tab
     * switch across the site. The rate limit above is what actually made that
     * affordable, and the scoping cost something real: the product page then had no
     * way to learn the cart had changed, so its cached snapshot — which now drives
     * the add-to-cart split, the warning text AND whether express checkout is
     * offered — went stale the moment another tab or the drawer touched the cart.
     * `releaseExpress` keeps its own cart-page guard, so nothing is stamped off-cart.
     */
    window.addEventListener('pageshow', function (e) { if (e.persisted) recheck(); });
    document.addEventListener('visibilitychange', function () {
      if (document.visibilityState === 'visible') recheck();
    });

    refresh();

    /**
     * Called by otl-preorder.js right after a split add.
     *
     * WHY (operator, 2026-07-16): "the popup that shows up in the cart when I press
     * checkout should come up when I try to add to cart." Letting a 10-piece
     * pre-order sit quietly in the cart and only rejecting it at checkout is a trap —
     * the customer has already moved on and has to come back and redo it.
     *
     * It does NOT block the add. The minimum is per colour ACROSS sizes, so a
     * customer building 20 White S + 30 White M must be able to add the first 20 —
     * blocking each add below 100 would make a 100-piece run impossible to build. So:
     * add, then immediately show the shortfall with steppers to fix it on the spot.
     */
    window.OTLPreorderCart = {
      promptIfShort: function () {
        return refresh().then(function () {
          if (lastBlocking && lastBlocking.length) showModal(lastBlocking, 'add');
        });
      },
      refresh: refresh,

      /** True when the last cart read failed, so the snapshot may be out of date. */
      snapshotIsStale: function () { return lastCartStale || !lastCart; },

      /**
       * Units of `variantId` already in the cart that were counted against STOCK —
       * i.e. unstamped lines. Stamped (`_preorder`) units are by definition beyond
       * stock and never consumed any, so subtracting them would over-count and push
       * genuinely in-stock pieces into the pre-order bucket.
       *
       * Returns 0 when the cart has never been read, which degrades the split to its
       * old stock-only behaviour rather than guessing.
       */
      claimedStockFor: function (variantId) {
        var base = lastCart && lastCart.items ? core.claimedStockFor(lastCart.items, variantId) : 0;
        var id = String(variantId);
        for (var i = 0; i < pendingClaims.length; i++) {
          if (String(pendingClaims[i].id) === id) base += pendingClaims[i].units;
        }
        return base;
      },

      /** Record stock an add just consumed, before `lastCart` catches up (see pendingClaims). */
      notePendingClaim: function (variantId, inStockUnits) {
        var n = parseInt(inStockUnits, 10);
        if (variantId == null || !n || n <= 0) return;
        pendingClaims.push({ id: variantId, units: n, at: now() });
      },

      /**
       * The run currently in the cart for one style+colour, or null.
       *
       * The product page uses this to show its progress bar only "once a run is
       * open for that colour" — a bar reading 0/100 on every out-of-stock product
       * would be noise, and worse, it would imply the customer had already started
       * something they had not.
       */
      groupFor: function (productId, color) {
        if (!lastCart || !lastCart.items) return null;
        var key = core.groupKeyFor(productId, color);
        var groups = core.groupPreorderLines(lastCart.items, MINIMUM);
        for (var i = 0; i < groups.length; i++) if (groups[i].key === key) return groups[i];
        return null;
      },

      /** The progress bar's markup, so the product page paints the SAME component. */
      runProgressHtml: function (group) { return core.runProgressHtml(group, MINIMUM); },

      /**
       * Open the run builder for a style+colour that may have NOTHING in the cart yet.
       *
       * This is where "Start a 100pc run" lands (decision 1 / D13). The builder was
       * written to render groups that already exist in the cart; a customer starting
       * from the product page has no group, so we hand it a synthetic empty one and
       * let the first stepper create the real lines.
       */
      openRunBuilder: function (spec) {
        if (!spec) return Promise.resolve();
        return refresh().catch(function () {}).then(function () {
          var existing = null;
          if (lastCart && lastCart.items) {
            var key = core.groupKeyFor(spec.productId, spec.color);
            var groups = core.groupPreorderLines(lastCart.items, MINIMUM);
            for (var i = 0; i < groups.length; i++) if (groups[i].key === key) existing = groups[i];
          }
          var group = existing || {
            key: core.groupKeyFor(spec.productId, spec.color),
            productId: spec.productId,
            handle: spec.handle || null,
            productTitle: spec.productTitle || null,
            styleLabel: core.styleLabel(spec.productTitle),
            color: spec.color == null ? null : spec.color,
            units: 0,
            url: null,
            lines: [],
            shortfall: MINIMUM,
          };
          // Seed the cache so the size grid paints on the FIRST render rather than
          // flashing an empty list — the product page has already fetched this exact
          // product, so there is nothing to wait for.
          if (spec.product && group.handle) productCache[group.handle] = spec.product;
          showModal([group], 'add');
        });
      },
    };
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
})();
